import type { ActionStreet, HandRecord, HandRecordAction } from '@tctp/protocol';
import { useState } from 'react';
import { PlayingCard } from './PlayingCard';

function nick(r: HandRecord, seat: number): string { return r.players.find(p => p.seat === seat)?.nickname ?? `Seat ${seat + 1}`; }
function wonBySeat(r: HandRecord): Map<number, number> {
  const totals = new Map<number, number>();
  for (const aw of r.awards) for (const sh of aw.shares) totals.set(sh.seat, (totals.get(sh.seat) ?? 0) + sh.amount);
  return totals;
}
function winners(r: HandRecord): string {
  return [...wonBySeat(r).entries()].map(([seat, amount]) => `${nick(r, seat)} wins ${amount}`).join(', ');
}
const pot = (r: HandRecord): number => r.awards.reduce((n, a) => n + a.amount, 0);

const STREETS: Array<{ street: ActionStreet; label: string; cards: number }> = [
  { street: 'preflop', label: 'Preflop', cards: 0 }, { street: 'flop', label: 'Flop', cards: 3 }, { street: 'turn', label: 'Turn', cards: 4 }, { street: 'river', label: 'River', cards: 5 },
];

/** One betting action as a chip: who, then what, coloured like the log lines. */
function ActionChip({ a, who, smallBlind }: { a: HandRecordAction; who: string; smallBlind: number }) {
  const auto = a.auto ? <small> timed out</small> : null;
  const [tone, what] = a.type === 'blind' ? ['blind', `${a.amount === smallBlind ? 'SB' : 'BB'} ${a.amount}`]
    : a.type === 'raise' ? ['raise', `raises to ${a.to}`]
    : a.type === 'call' ? ['call', `calls ${a.amount}`]
    : a.type === 'fold' ? ['fold', 'folds']
    : ['check', 'checks'];
  return <span className={`act ${tone}`}><span className="who">{who}</span> {what}{auto}</span>;
}

/** The hand street by street: who did what, the board as it grew, the pot after each street, and the showdown. */
function Breakdown({ r, onReplay }: { r: HandRecord; onReplay: (r: HandRecord) => void }) {
  const won = wonBySeat(r);
  const betting = r.actions.filter(a => a.type !== 'show' && a.type !== 'discardLocked').map(a => ({ ...a, street: a.street ?? 'preflop' }));
  let running = 0;
  const rows = STREETS.flatMap(({ street, label, cards }) => {
    const acts = betting.filter(a => a.street === street);
    if (acts.length === 0 && r.board.length < cards) return [];   // the hand ended before this street
    if (acts.length === 0 && cards === 0) return [];
    for (const a of acts) running += a.amount ?? 0;
    const shown = r.board.slice(0, cards);
    const from = cards === 3 ? 0 : cards === 4 ? 3 : cards === 5 ? 4 : 0;
    return [(
      <div className="hb-row" key={street}>
        <span className="hb-street">{label}</span>
        <span className="hb-cards">{shown.map((o, i) => <PlayingCard key={o.position} card={o.card} size="xs" className={i >= from ? 'new' : 'old'} />)}</span>
        <span className="hb-acts">{acts.map((a, i) => <ActionChip key={i} a={a} who={nick(r, a.seat)} smallBlind={r.smallBlind} />)}{acts.length === 0 && <span className="muted">no betting</span>}</span>
        <span className="hb-pot">pot {running}</span>
      </div>
    )];
  });
  const showdownSeats = new Set(r.showdown.map(s => s.seat));
  const shared = r.board2 ? r.board2.findIndex((o, i) => r.board[i]?.card !== o.card) : 0;   // run twice: the leading cards both boards share
  const run2 = r.board2 && (
    <div className="hb-row" key="run2">
      <span className="hb-street">Run 2</span>
      <span className="hb-cards">{r.board2.map((o, i) => <PlayingCard key={o.position} card={o.card} size="xs" className={i >= (shared < 0 ? r.board2!.length : shared) ? 'new' : 'old'} />)}</span>
      <span className="hb-acts"><span className="muted">second board, pot split between the runs</span></span>
      <span className="hb-pot">pot {pot(r)}</span>
    </div>
  );
  return (
    <div className="hb">
      <div className="hb-row deal">
        <span className="hb-street">Deal</span>
        <span className="hb-acts">
          {r.players.map(p => {
            const d = r.discards.find(x => x.seat === p.seat);
            return (
              <span key={p.seat} className={`hb-player${p.seat === r.buttonSeat ? ' button' : ''}`}>
                <span className="who">{p.nickname}</span> <span className="muted">{p.startStack}</span>
                {p.seat === r.buttonSeat && <span className="hb-dealer" title="dealer">D</span>}
                {d && <span className="hb-discard">discards <PlayingCard card={d.opening.card} size="xs" /></span>}
              </span>
            );
          })}
        </span>
        <span className="hb-pot"><button type="button" className="hb-replay" onClick={() => onReplay(r)}>▶ REPLAY</button></span>
      </div>
      {rows}
      {run2}
      <div className="hb-row showdown">
        <span className="hb-street">{r.showdown.length > 0 ? 'Showdown' : 'Result'}</span>
        <span className="hb-acts">
          {r.showdown.map(s => (
            <span key={s.seat} className={`hb-hand${won.has(s.seat) ? ' winner' : ''}`}>
              <span className="who">{nick(r, s.seat)}</span>
              {s.keepers.map(c => <PlayingCard key={c} card={c} size="xs" />)}
              <span className="hb-name">{r.showdown2 ? `#1 ${s.categoryName}` : s.categoryName}</span>
              {r.showdown2 && <span className="hb-name">#2 {r.showdown2.find(x => x.seat === s.seat)?.categoryName ?? '—'}</span>}
              {won.has(s.seat) && <span className="hb-won">+{won.get(s.seat)}</span>}
            </span>
          ))}
          {r.shows.map(s => (
            <span key={`v${s.seat}`} className="hb-hand">
              <span className="who">{nick(r, s.seat)}</span>
              {s.cards.map(c => <PlayingCard key={c} card={c} size="xs" />)}
              <span className="hb-name muted">showed</span>
            </span>
          ))}
          {[...won.entries()].filter(([seat]) => !showdownSeats.has(seat)).map(([seat, amount]) => (
            <span key={`w${seat}`} className="hb-hand winner"><span className="who">{nick(r, seat)}</span><span className="hb-name">everyone else folded</span><span className="hb-won">+{amount}</span></span>
          ))}
          {r.refund && <span className="hb-hand"><span className="who">{nick(r, r.refund.seat)}</span><span className="hb-name muted">uncalled {r.refund.amount} returned</span></span>}
        </span>
        <span className="hb-pot">pot {pot(r)}</span>
      </div>
    </div>
  );
}

/** `compact`: a tap on a hand opens its replay instead of the breakdown (phones). */
export function HandsTab({ history, onReplay, compact = false }: { history: HandRecord[]; onReplay: (r: HandRecord) => void; compact?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  if (history.length === 0) return <p className="muted">No hands yet.</p>;
  return (
    <ul className="hands">
      {[...history].reverse().map(r => (
        <li key={r.handId} className={open === r.handId ? 'open' : ''}>
          <button type="button" className="hand-head" aria-expanded={compact ? undefined : open === r.handId} onClick={() => (compact ? onReplay(r) : setOpen(open === r.handId ? null : r.handId))}>
            <span className="hand-num">Hand #{r.handNumber}</span> <span className="muted">{new Date(r.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span> <span>pot {pot(r)}</span> <span>{winners(r)}</span>
            <span className="hand-toggle" aria-hidden="true">{compact ? '▶' : open === r.handId ? '▴' : '▾'}</span>
          </button>
          {open === r.handId ? <Breakdown r={r} onReplay={onReplay} /> : (
            <>
              <div className="hand-board">{r.board.map(o => <PlayingCard key={o.position} card={o.card} size="xs" />)}</div>
              {r.discards.length > 0 && <div className="hand-discards">{r.discards.map(d => <span key={d.seat}>{nick(r, d.seat)} discarded <PlayingCard card={d.opening.card} size="xs" /></span>)}</div>}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
